import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlanPricesEditor } from "@/components/settings/PlanPricesEditor";
import { RulesEditor } from "@/components/settings/RulesEditor";
import { MatricesEditor } from "@/components/settings/MatricesEditor";
import { FormulaEditor } from "@/components/settings/FormulaEditor";
import { ClassTablesEditor } from "@/components/settings/ClassTablesEditor";
import { SelectionOptions } from "@/components/SelectionOptions";

export default function Settings() {
  const [s, setS] = useState(null);
  const [fields, setFields] = useState([]);
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/settings"), api.get("/meta")])
      .then(([a, b]) => { setS(a.data); setFields(b.data.raw_fields); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", s);
      setS(data);
      toast.success("Settings saved. All races re-rated.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset rules, matrices and formula to defaults?")) return;
    const { data } = await api.post("/settings/reset");
    setS(data);
    setVersion((v) => v + 1);
    toast.success("Defaults restored");
  };

  if (!s) return <div className="py-20 grid place-items-center"><div className="loader-bar" /></div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Rule Engine</div>
          <h1 className="text-4xl sm:text-5xl font-bold">Rules, Matrices & Pricing</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} data-testid="settings-reset-button"><RotateCcw className="h-4 w-4 mr-1.5" />Reset Defaults</Button>
          <Button onClick={save} disabled={busy} data-testid="settings-save-button" className="font-semibold"><Save className="h-4 w-4 mr-1.5" />{busy ? "Saving..." : "Save Configuration"}</Button>
        </div>
      </div>
      <div className="panel p-5 max-w-2xl" data-testid="settings-selection-options">
        <SelectionOptions onChange={() => api.get("/settings").then((a) => { setS(a.data); setVersion((v) => v + 1); })} />
      </div>
      <Tabs defaultValue="rules" key={version}>
        <TabsList className="bg-[#0F172A] border border-slate-800 flex-wrap h-auto justify-start">
          <TabsTrigger value="rules" data-testid="settings-tab-rules">Selection Rules</TabsTrigger>
          <TabsTrigger value="class" data-testid="settings-tab-class">Class Scores</TabsTrigger>
          <TabsTrigger value="matrices" data-testid="settings-tab-matrices">Rating Rules</TabsTrigger>
          <TabsTrigger value="formula" data-testid="settings-tab-formula">Price Formula</TabsTrigger>
          <TabsTrigger value="plans" data-testid="settings-tab-plans">Plan Prices</TabsTrigger>
        </TabsList>
        <TabsContent value="rules" className="mt-6 panel p-6">
          <RulesEditor rules={s.rules} onChange={(rules) => setS({ ...s, rules })} />
        </TabsContent>
        <TabsContent value="class" className="mt-6">
          <ClassTablesEditor tracks={s.tracks} tables={s.class_tables} onTracks={(tracks) => setS({ ...s, tracks })} onTables={(class_tables) => setS({ ...s, class_tables })} letters={s.letter_scores} onLetters={(letter_scores) => setS({ ...s, letter_scores })} />
        </TabsContent>
        <TabsContent value="matrices" className="mt-6">
          <MatricesEditor matrices={s.matrices} fields={fields} onChange={(matrices) => setS({ ...s, matrices })} />
        </TabsContent>
        <TabsContent value="formula" className="mt-6 panel p-6">
          <FormulaEditor formula={s.formula} onChange={(formula) => setS({ ...s, formula })} />
        </TabsContent>
        <TabsContent value="plans" className="mt-6 panel p-6">
          <PlanPricesEditor />
        </TabsContent>
      </Tabs>
    </div>
  );
}
